export const USER_PREFERENCE_TYPES = Object.freeze({
  BOOLEAN: 'boolean',
  ENUM: 'enum',
  NUMBER: 'number',
})

export const findUserPreferenceDefinition = (registry, key) => registry.find((definition) => definition.key === key)

const isValidNumber = (definition, value) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return false
  if (typeof definition.min === 'number' && value < definition.min) return false
  if (typeof definition.max === 'number' && value > definition.max) return false
  const step = definition.step ?? 1
  return !Number.isInteger(step) || Number.isInteger(value)
}

export const isValidUserPreferenceValue = (definition, value) => {
  switch (definition.type) {
    case USER_PREFERENCE_TYPES.BOOLEAN:
      return typeof value === 'boolean'
    case USER_PREFERENCE_TYPES.ENUM:
      return (definition.options || []).some((option) => option.value === value)
    case USER_PREFERENCE_TYPES.NUMBER:
      return isValidNumber(definition, value)
    default:
      return false
  }
}

export const isUserPreferenceLocked = (definition, getters) => Boolean(definition.plan)
  && !getters[definition.plan.entitledGetter]

export const ADVANCED_SETTINGS_SECTION_FLAG = 'CORE-31-advanced-settings'

export const isAdvancedSettingsSectionEnabled = (getters) => Boolean(
  getters['featureToggles/isFeatureEnabled']?.(ADVANCED_SETTINGS_SECTION_FLAG),
)

export const isUserPreferenceVisible = (definition, getters) => !definition.featureFlag
  || Boolean(getters['featureToggles/isFeatureEnabled']?.(definition.featureFlag))

export const isUserPreferenceEarlyAccess = (definition, getters) => !isUserPreferenceVisible(definition, getters)
  && definition.searchableBeforeRollout !== false

export const hasStoredUserPreference = (definition, preferences) => (
  isValidUserPreferenceValue(definition, preferences?.[definition.key])
)

export const isUserPreferenceSearchable = (definition, getters) => isUserPreferenceVisible(definition, getters)
  || isUserPreferenceEarlyAccess(definition, getters)

export const isUserPreferenceAvailable = (definition, getters) => isUserPreferenceSearchable(definition, getters)
  && !isUserPreferenceLocked(definition, getters)

export const resolveUserPreferenceValue = (definition, preferences, getters) => {
  if (!isUserPreferenceAvailable(definition, getters)) return definition.default
  return hasStoredUserPreference(definition, preferences) ? preferences[definition.key] : definition.default
}

export const getVisibleUserPreferenceDefinitions = (registry, getters, preferences) => registry.filter(
  (definition) => isUserPreferenceVisible(definition, getters)
    || (isUserPreferenceEarlyAccess(definition, getters) && hasStoredUserPreference(definition, preferences)),
)

export const getSearchableUserPreferenceDefinitions = (registry, getters) => registry
  .filter((definition) => isUserPreferenceSearchable(definition, getters))

/** Ungrouped definitions come first, then each group in the order its first entry appears. */
export const groupUserPreferenceDefinitions = (definitions) => {
  const groups = new Map([[null, []]])
  definitions.forEach((definition) => {
    const label = definition.group || null
    if (!groups.has(label)) groups.set(label, [])
    groups.get(label).push(definition)
  })
  return [...groups.entries()]
    .filter(([, members]) => members.length > 0)
    .map(([label, members]) => ({ label, definitions: members }))
}
